import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Steps, Step } from "@/components/mt/MTBox";
import { ScaleExplorer } from "@/components/mt/ScaleExplorer";
import { NoteImage } from "@/components/mt/NoteImage";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory4() {
  const { lesson, course, accent } = findLesson("music-theory", "4");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT4-Major-Minor-Animated.mp4"
        caption="Major ↔ Minor — flatten the 3rd and the 6th, then choose your 7th"
        hint="≈ 21 sec · watch first"
      />
      <Chapter number="04" eyebrow="Music Theory · Chapter Four" title="The major-to-minor party trick">
        <p>
          Take any happy melody you know — Twinkle, Happy Birthday, Mary Had a Little Lamb — and
          turn it sad. Or take a brooding minor-key tune and lift it into sunlight. The conversion
          is mechanical, fast, and only really requires you to know <em>two</em> notes per
          scale: the 3rd and the 6th. Once you have that move in your fingers, you can flip the
          mood of any tune in real time.
        </p>
        <MTQuote>
          In this lesson we are going to do a very fun challenge, which is to convert any
          song or any melody which is in the major scale, which is rather happy, into the
          minor scale, which is in the more serious or the sad side. And you can even go
          vice versa.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="The conversion rule" title="Two notes do almost all the work">
        <Prose>
          <p>
            The rule for major → minor is the same one we learned in the previous chapter, but
            now we use it on melodies, not just on scales. Wherever the melody plays the
            <strong> 3rd</strong> of the scale, lower it by one semitone. Wherever it plays the
            <strong> 6th</strong>, lower that too. Leave the 7th alone for now (we'll come back
            to it).
          </p>
        </Prose>

        <TheoryBox label="The flip">
          <div className="text-center font-mt text-xl text-amber-200">
            <strong>Major → Minor: flatten the 3rd and the 6th</strong>
          </div>
          <div className="mt-2 text-center text-[13px] text-nsm-dim">
            (Minor → Major: raise them. Same operation in reverse.)
          </div>
        </TheoryBox>

        <Prose>
          <p>
            That single edit is where the mood flips. The 3rd alone is
            already responsible for "happy versus sad" — once you flatten it, the harmony has
            already swung. The 6th sweetens or sharpens the colour further. The 7th is a stylistic
            choice between natural minor (♭7) and harmonic minor (natural 7).
          </p>
        </Prose>

        <NoteImage
          src="/notes/music-theory/finals/04a-convert-major-to-minor-steps.png"
          caption="Jason's note: how to convert any major-key melody into minor. Write the major version first, then re-write it underneath using ♭3 ♭6 ♭7 and play. The example shows C major → C minor across two phrase lines, with the same numbered shape (5 5 6 5 1 7 …) producing the bright original and then the brooding minor flip."
        />
      </Chapter>

      <Chapter eyebrow="Worked example" title="Twinkle Twinkle in F — major to minor">
        <Prose>
          <p>
            We'll use Twinkle Twinkle, written in F major. The number form (from Chapter 2) is:
          </p>
        </Prose>

        <TheoryBox label="Twinkle in numbers">
          <div className="text-center font-mt text-xl text-amber-200">
            <strong>1 1 5 5 6 6 5 &nbsp;·&nbsp; 4 4 3 3 2 2 1</strong>
          </div>
        </TheoryBox>

        <Prose>
          <p>
            On F major (<span className="font-mono">F G A B♭ C D E</span>) that becomes:
            <span className="font-mono text-amber-300"> F F C C D D C &nbsp;·&nbsp; B♭ B♭ A A G G F</span>.
          </p>
          <p>
            Now apply the conversion. Wherever you see a <strong>3</strong> (which is A in F
            major), play <strong>♭3</strong> (A♭). Wherever you see a <strong>6</strong> (which
            is D in F major), play <strong>♭6</strong> (D♭). The new melody:
          </p>
        </Prose>

        <Steps>
          <Step><strong>1 = F</strong> · unchanged.</Step>
          <Step><strong>5 = C</strong> · unchanged.</Step>
          <Step><strong>6 → ♭6 = D♭</strong> · the 6th gets flattened.</Step>
          <Step><strong>4 = B♭</strong> · unchanged.</Step>
          <Step><strong>3 → ♭3 = A♭</strong> · the 3rd gets flattened.</Step>
          <Step><strong>2 = G</strong> · unchanged.</Step>
          <Step><strong>1 = F</strong> · unchanged. Final landing.</Step>
        </Steps>

        <Prose>
          <p>
            Play it. Twinkle Twinkle, suddenly, is a meditation. Same melody, same shape, with
            two of its notes flattened — and the song has a completely different emotional life. Try it on
            Happy Birthday. Try it on the wedding march. The trick never stops being satisfying.
          </p>
        </Prose>

        <ScaleExplorer tonic="F" scales={["major", "naturalMinor"]} />
      </Chapter>

      <Chapter eyebrow="The 7th" title="Natural minor or harmonic minor?">
        <Prose>
          <p>
            We left the 7th deliberately untouched in the rule because it gives you a
            stylistic choice:
          </p>
          <ul className="space-y-1 text-[14px]">
            <li>
              Leave the 7 <strong>natural</strong> (same as the major scale) and you have a
              <strong> harmonic-minor</strong> flavour — that more dramatic, almost classical
              colour we met in Chapter 3.
            </li>
            <li>
              Flatten the 7 to ♭7 and you have a <strong>natural-minor</strong> flavour — softer,
              folkier, more pop-ballad-friendly.
            </li>
          </ul>
          <p>
            Either choice is correct. The harmonic-minor version sounds more "old-world" or
            "fairy-tale"; the natural-minor version sounds more "introspective". Try both on the
            same melody and decide which one fits the emotion you're going for.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The other direction" title="Minor → major: raise 3 and 6">
        <Prose>
          <p>
            The conversion runs in reverse just as cleanly. Find a sad melody — say a folk dirge
            or a minor-key lullaby — and find every ♭3 and ♭6 in it. Raise them by one semitone.
            The melody becomes major, brighter, optimistic. It often sounds wrong at first
            because you've spent your whole life hearing it the other way. That's the point: you
            have stripped its emotional fingerprint and reprinted a new one.
          </p>
          <p>
            This is why composers use the major-minor flip as a structural device. A song might
            sit in a minor key for a verse, then "lift" into the parallel major for the chorus.
            That single move is one of the most reliable emotional gestures in pop music.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The left hand follows" title="Major chord vs. minor chord">
        <Prose>
          <p>
            When you accompany a major-key melody with a left-hand chord, you typically play the
            <strong> major triad</strong> of the home key: root + 3rd + 5th. On F that's F–A–C.
            When the melody flips to minor, the chord must follow: keep the root, keep the 5th,
            but flatten the 3rd. F major (F–A–C) becomes F minor (F–A♭–C). One note changes; the
            colour swings.
          </p>
          <p>
            Lock that left-hand chord into a steady pulse — quarter notes, dotted rhythms,
            anything that breathes — and even the simplest melody starts to sound like a
            performance. Major and minor chords are the topic of Chapter 5; for now, just notice
            that the same flip-the-3rd rule applies to chords as well as scales.
          </p>
        </Prose>

        <TheoryBox label="The chord flip">
          <div className="text-center font-mt text-xl text-amber-200">
            <strong>Major triad → Minor triad: flatten the 3rd</strong>
          </div>
          <div className="mt-2 text-center text-[13px] text-nsm-dim">
            (F–A–C becomes F–A♭–C; same trick at chord level)
          </div>
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Practice plan" title="Re-flavour everything you already play">
        <Prose>
          <p>
            Take three melodies you already know in major and convert each one to minor. Twinkle,
            Happy Birthday, Mary Had a Little Lamb are perfect first candidates. Then take three
            you know in minor (House of the Rising Sun, Hava Nagila, Greensleeves) and convert
            them to major. Play with both natural-minor and harmonic-minor versions and listen
            for the personality difference.
          </p>
          <p>
            Add a left-hand chord — major in the major version, minor in the minor version. The
            song now sounds finished, performed, intentional. You haven't learned a new tune —
            you've learned a new <em>vocabulary</em> for every tune you'll ever play.
          </p>
        </Prose>
      </Chapter>

      <NextUp
        to="/lesson/music-theory-5"
        label="Chapter 5 · Chord Theory"
        why="With major and minor flips at your fingertips, the next move is to build full chord families on every degree of the scale — and learn which ones are happy, sad, and tense."
      />
    </LessonShell>
  );
}
